"use client";

import type { ButtonHTMLAttributes, HTMLAttributes, MouseEvent } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import {
  type ActiveProp,
  type BorderProp,
  type ChildrenProp,
  type ClassNameProp,
  type EdgesProps,
  polymorphicForwardRef,
  type TrackingProps,
} from "../../props";
import { Edges } from "../Edges/Edges";
import { Text, type TextProps } from "../Text";
import { useTracker } from "../../hooks/useTracker";
import { useParentHover } from "../../hooks";

import {
  buttonContainerStyle,
  buttonEdgesStyle,
  buttonTextStyle,
  buttonVariantStyle,
} from "./Button.css";
import { buttonStyle } from "./ButtonStyle.css";
import { borders } from "../../theme/css/borders.css";
import { focusVisibleInset } from "../../theme/css/focusVisible.css";

export type ButtonProps<
  T extends HTMLAttributes<Element> = ButtonHTMLAttributes<HTMLButtonElement>,
> = T &
  ChildrenProp &
  ClassNameProp &
  BorderProp &
  ActiveProp &
  TrackingProps &
  RecipeVariants<typeof buttonContainerStyle> &
  RecipeVariants<typeof buttonVariantStyle> &
  EdgesProps & {
    align?: TextProps["align"];
  };

/**
 * Button component.
 */
export const Button = polymorphicForwardRef<"button", ButtonProps>(
  (
    {
      as: Element = "button",
      type = "button",
      variant = "primary",
      active = false,
      width = "auto",
      grow = false,
      trailing,
      leading,
      border,
      size,
      tracking,
      align,
      alignSelf,
      justifySelf,
      className,
      children,
      onClick,
      ...props
    },
    forwardedRef,
  ) => {
    const { track } = useTracker();
    const { parentHover } = useParentHover();

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (tracking) {
        track("interaction", {
          category: "button",
          action: "click",
          label: tracking,
          properties: {
            buttonId: tracking,
            buttonText: typeof children === "string" ? children : undefined,
            buttonType: variant,
            buttonSize: size,
          },
        });
      }
    };

    return (
      <Element
        ref={forwardedRef}
        className={mergeClasses(
          buttonStyle,
          buttonContainerStyle({ size, width, alignSelf, justifySelf, grow }),
          buttonVariantStyle({ variant }),
          className,
          border ? borders[border] : undefined,
          focusVisibleInset,
        )}
        onClick={handleClick}
        data-tracking={tracking}
        type={type}
        {...props}
        {...(active || parentHover ? { "data-active": "true" } : {})}
      >
        <Edges
          className={buttonEdgesStyle}
          trailing={trailing}
          leading={leading}
          hideOverflow
        >
          {children && (
            <Text
              align={align}
              ellipsis
              className={buttonTextStyle({ size })}
              bold={false}
            >
              {children}
            </Text>
          )}
        </Edges>
      </Element>
    );
  },
);

Button.displayName = "Button";
